import { tv } from 'tailwind-variants';

import { type Color } from '../util';
import type { AvatarSize, AvatarType } from './Avatar.types';

export const avatar = tv({
	slots: {
		base: 'relative flex items-center justify-center overflow-hidden',
		fallback: 'flex items-center justify-center',
		text: 'font-semibold leading-none text-white',
	},
	variants: {
		color: {
			gray: { fallback: 'bg-gray-500' },
			'gray-light': { fallback: 'bg-gray-400' },
			indigo: { fallback: 'bg-indigo-500' },
			'indigo-light': { fallback: 'bg-indigo-300' },
			blue: { fallback: 'bg-blue-500' },
			yellow: { fallback: 'bg-yellow-500' },
			red: { fallback: 'bg-red-500' },
			'red-light': { fallback: 'bg-red-300' },
			green: { fallback: 'bg-green-500' },
		} satisfies Record<Color, { fallback: string }>,
		size: {
			'extra-small': { base: 'size-6', text: 'text-xs' },
			small: { base: 'size-8', text: '' },
			medium: { base: 'size-10', text: 'text-base' },
			large: { base: 'size-12', text: 'text-base' },
		} satisfies Record<AvatarSize, { base: string; text: string }>,
		// `text-*` on fallback drives the icon's color via `currentColor` cascade on web (see Avatar.web.tsx —
		// no `color` prop needed there). Native ignores it (`color` isn't a valid View style) and still gets
		// its icon color from the literal getIconColor() value below, since there's no CSS cascade there.
		type: {
			circle: { base: 'rounded-full', fallback: 'rounded-full text-white' },
			square: { base: 'rounded', fallback: 'rounded bg-gray-100 text-gray-500' },
		} satisfies Record<AvatarType, { base: string; fallback: string }>,
	},
	defaultVariants: {
		size: 'medium',
		type: 'circle',
	},
});

const ICON_SIZE_BY_AVATAR_SIZE: Record<AvatarSize, number> = {
	'extra-small': 12,
	small: 16,
	medium: 16,
	large: 24,
};

export const getIconSize = (size: AvatarSize = 'small') => ICON_SIZE_BY_AVATAR_SIZE[size];

export const getIconColor = (type: AvatarType = 'circle') => (type === 'square' ? 'slategray' : 'white');
